import React, { useEffect, useMemo, useState } from "react";
import { Approve } from "../../api/approved_chk";
import {
  ApprovedStatusId,
  DirectorLevel,
  HRLevel,
  TYLevel,
} from "../../constants";

const STATUS = {
  1: { label: "Не согласовано", tone: "wait" },
  2: { label: "На доработке", tone: "rework" },
  3: { label: "Согласовано", tone: "ok" },
};

const STAGES = [
  {
    key: "director",
    title: "Директор",
    statusKey: "director_status",
    nameKey: "director_surname_name",
    commentKey: "director_comment",
    level: DirectorLevel,
  },
  {
    key: "ty",
    title: "ТУ",
    statusKey: "TY_status",
    nameKey: "TY_surname_name",
    commentKey: "TY_comment",
    level: TYLevel,
  },
  {
    key: "hr",
    title: "HR",
    statusKey: "HR_status",
    nameKey: "HR_surname_name",
    commentKey: "HR_comment",
    level: HRLevel,
  },
];

function formatShortDate(dateString) {
  if (!dateString) return "";
  const iso = String(dateString).slice(0, 10);
  const [, month, day] = iso.split("-");
  return day && month ? `${day}.${month}` : iso;
}

function statusMeta(statusId) {
  return STATUS[Number(statusId)] || STATUS[1];
}

function fieldsForRole(data, accessLevel) {
  if (Number(accessLevel) === HRLevel) {
    return {
      status_id: String(data?.HR_status || 1),
      comment: data?.HR_comment || "",
    };
  }
  if (Number(accessLevel) === TYLevel) {
    return {
      status_id: String(data?.TY_status || 1),
      comment: data?.TY_comment || "",
    };
  }
  if (Number(accessLevel) === DirectorLevel) {
    return {
      status_id: String(data?.director_status || 1),
      comment: data?.director_comment || "",
    };
  }
  return { status_id: "1", comment: "" };
}

function overallCopy(data, period = "week") {
  const ty = Number(data?.TY_status);
  const director = Number(data?.director_status);
  const hr = Number(data?.HR_status);
  const isMonth = period === "month";
  if (ty === ApprovedStatusId && hr === ApprovedStatusId && director === ApprovedStatusId) {
    return {
      tone: "ok",
      title: isMonth ? "Месяц полностью согласован" : "Неделя полностью согласована",
      text: "Директор, ТУ и HR подтвердили график.",
    };
  }
  if (ty === ApprovedStatusId) {
    return {
      tone: "ok",
      title: "Согласовано ТУ",
      text: isMonth
        ? "Месяц подтверждён территориальным управляющим."
        : "Дальше правки только через служебную записку.",
    };
  }
  if (director === ApprovedStatusId) {
    return {
      tone: "rework",
      title: "Ждёт ТУ",
      text: "Директор согласовал, территориальный управляющий ещё нет.",
    };
  }
  if (ty === 2 || director === 2 || hr === 2) {
    return {
      tone: "rework",
      title: "На доработке",
      text: "Кто-то из цепочки вернул график на доработку.",
    };
  }
  return {
    tone: "wait",
    title: "Ещё не согласовано",
    text: isMonth
      ? "Статусы директора, ТУ и HR по этому месяцу."
      : "Статусы директора, ТУ и HR по этой неделе.",
  };
}

const ScheduleChkApprove = ({
  data,
  weekDates = [],
  accessLevel,
  token,
  onUpdated,
  approveRequest = Approve,
  period = "week",
}) => {
  const canEdit = [DirectorLevel, TYLevel, HRLevel].includes(Number(accessLevel));
  const myStage = STAGES.find((stage) => stage.level === Number(accessLevel));
  const [form, setForm] = useState(() => fieldsForRole(data, accessLevel));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    setForm(fieldsForRole(data, accessLevel));
    setMessage(null);
  }, [data, accessLevel]);

  const summary = useMemo(() => overallCopy(data, period), [data, period]);
  const weekLabel =
    weekDates[0] && weekDates.at(-1)
      ? `${formatShortDate(weekDates[0])} — ${formatShortDate(weekDates.at(-1))}`
      : "";

  const save = async () => {
    if (!weekDates[0]) {
      setMessage({ ok: false, text: "Не выбрана дата" });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      const result = await approveRequest(
        token,
        weekDates[0],
        weekDates.at(-1),
        Number(form.status_id) || 1,
        form.comment ?? "",
      );
      if (result?.detail && !Number.isFinite(result?.id)) {
        setMessage({ ok: false, text: result.detail });
        return;
      }
      onUpdated?.(result);
      setMessage({ ok: true, text: "Сохранено" });
    } catch (err) {
      console.error(err);
      setMessage({ ok: false, text: "Не удалось сохранить" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="chk-approve">
      <div className={`chk-approve-summary is-${summary.tone}`}>
        <div>
          <h4>{summary.title}</h4>
          <p>{summary.text}</p>
        </div>
        {weekLabel ? <span className="chk-approve-week">{weekLabel}</span> : null}
      </div>

      <div className="chk-approve-pipeline">
        {STAGES.map((stage, index) => {
          const statusId = data?.[stage.statusKey];
          const meta = statusMeta(statusId);
          const isMine = myStage?.key === stage.key;
          return (
            <React.Fragment key={stage.key}>
              {index > 0 ? <div className="chk-approve-arrow" aria-hidden="true" /> : null}
              <article
                className={`chk-approve-card is-${meta.tone} ${
                  isMine ? "is-mine" : ""
                }`}
              >
                <header>
                  <span>{stage.title}</span>
                  {isMine ? <em>вы</em> : null}
                </header>
                <strong className={`chk-approve-badge is-${meta.tone}`}>
                  {meta.label}
                </strong>
                <p className="chk-approve-name">
                  {data?.[stage.nameKey] || "Ещё не указан"}
                </p>
                <p className="chk-approve-comment">
                  {data?.[stage.commentKey] || "Нет комментария"}
                </p>
              </article>
            </React.Fragment>
          );
        })}
      </div>

      {canEdit ? (
        <div className="chk-approve-form">
          <h5>
            Ваше согласование
            {myStage ? ` · ${myStage.title}` : ""}
          </h5>
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label" htmlFor="chk-approve-status">
                Статус
              </label>
              <select
                id="chk-approve-status"
                className="form-select"
                value={form.status_id}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, status_id: event.target.value }))
                }
              >
                <option value="1">Не согласовано</option>
                <option value="2">На доработке</option>
                <option value="3">Согласовано</option>
              </select>
            </div>
            <div className="col-md-8">
              <label className="form-label" htmlFor="chk-approve-comment">
                Комментарий
              </label>
              <textarea
                id="chk-approve-comment"
                className="form-control"
                rows={3}
                value={form.comment}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, comment: event.target.value }))
                }
              />
            </div>
          </div>
          <div className="chk-approve-form-footer">
            {message ? (
              <span className={message.ok ? "is-ok" : "is-error"}>{message.text}</span>
            ) : (
              <span />
            )}
            <button
              type="button"
              className="btn"
              onClick={save}
              disabled={saving}
            >
              {saving ? "Сохранение…" : "Сохранить"}
            </button>
          </div>
        </div>
      ) : (
        <p className="chk-approve-readonly">
          Менять статус могут директор, ТУ и HR.
        </p>
      )}
    </div>
  );
};

export default ScheduleChkApprove;
